/**
 * Mount + control the Telegram MainButton for the lifetime of a
 * component.
 *
 * Pattern:
 *
 *     useMainButton({
 *       text: isSaving ? "Saving…" : "Create project",
 *       loading: isSaving,
 *       disabled: !canSubmit,
 *       onClick: handleSubmit,
 *     });
 *
 * The hook re-applies `text` / `loading` / `disabled` whenever any of
 * them changes, and rebinds the click handler when its identity
 * changes (so callers should `useCallback` it).
 *
 * Outside Telegram (browser dev), `isSupported()` is false and the
 * hook is a no-op — no errors, no UI side effects.
 */
import { useEffect } from "react";
import { mountMainButton, mainButton } from "@telegram-apps/sdk-react";

interface Options {
  text: string;
  onClick: () => void;
  /** Defaults to true; false hides the button. */
  visible?: boolean;
  /** Show the spinner overlay on the button. */
  loading?: boolean;
  /** Greys the button and ignores clicks. */
  disabled?: boolean;
}

export function useMainButton(opts: Options): void {
  const { text, onClick, visible = true, loading = false, disabled = false } = opts;

  // Mount lifecycle — only run once per component instance.
  useEffect(() => {
    if (!mountMainButton.isAvailable()) return;
    if (!mainButton.isMounted()) {
      mountMainButton();
    }
    return () => {
      // Hide on unmount, but don't unmount the global (other pages
      // may want it). Hiding is enough to keep the chrome clean.
      mainButton.setParams({ isVisible: false });
    };
  }, []);

  // Apply visual state on every relevant change.
  useEffect(() => {
    if (!mainButton.isMounted()) return;
    mainButton.setParams({
      text,
      isVisible: visible,
      isLoaderVisible: loading,
      isEnabled: !disabled,
    });
  }, [text, visible, loading, disabled]);

  // Click handler — rebind only when callback identity changes.
  useEffect(() => {
    if (!mainButton.isMounted()) return;
    const off = mainButton.onClick(onClick);
    return () => {
      off();
    };
  }, [onClick]);
}
